import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — parses a target number from a string like "1200+" or "15+"
 * and eases from 0 to the number when it becomes visible. The trailing
 * non-numeric characters (e.g. "+") are preserved.
 *
 * Props:
 *  - value: string containing a number, may include prefix/suffix
 *  - duration: ms (default 1600)
 *  - className: passthrough
 */
export default function AnimatedCounter({ value, duration = 1600, className = "" }) {
  const ref = useRef(null);
  const [display, setDisplay] = useState(null);

  useEffect(() => {
    const str = String(value ?? "");
    const match = str.match(/(-?[\d.,]+)/);
    if (!match) {
      setDisplay(str);
      return;
    }
    const target = parseFloat(match[1].replace(/,/g, ""));
    const prefix = str.slice(0, match.index);
    const suffix = str.slice(match.index + match[1].length);
    const isInt = Number.isInteger(target);

    const prefersReduced =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;

    if (prefersReduced) {
      setDisplay(`${prefix}${match[1]}${suffix}`);
      return;
    }

    setDisplay(`${prefix}0${suffix}`);

    const el = ref.current;
    if (!el) return;
    let started = false;
    let raf;
    let startTime;

    const step = (now) => {
      if (!startTime) startTime = now;
      const p = Math.min(1, (now - startTime) / duration);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - p, 3);
      const current = target * eased;
      const rendered = isInt ? Math.round(current).toString() : current.toFixed(1);
      setDisplay(`${prefix}${rendered}${suffix}`);
      if (p < 1) raf = requestAnimationFrame(step);
    };

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !started) {
            started = true;
            raf = requestAnimationFrame(step);
            obs.unobserve(e.target);
          }
        });
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => {
      obs.disconnect();
      if (raf) cancelAnimationFrame(raf);
    };
  }, [value, duration]);

  return (
    <span ref={ref} className={className}>
      {display}
    </span>
  );
}
